import { z } from 'zod';

/**
 * Render-critical zod schemas for the `fe_config` form-field arrays and label
 * tree. These mirror the FE TypeScript contracts (`FormFieldConfig`,
 * `PromoFieldConfig`, `Labels`) at the strictness needed to render safely:
 *
 * - Only fields that the UI dereferences without guarding are REQUIRED, so a
 *   malformed backend entry fails loud at the config gate (with a precise path)
 *   instead of crashing deep inside a component (e.g. `field.label.replace`).
 * - Every object uses `.passthrough()` so save-only / behavioural fields
 *   (`apiKey`, `transform`, `onEmpty`, `responseKey`, `offerMapping`, the many
 *   optional flags, plus event/plan submission extras) survive validation into
 *   `feConfigAdapter` and the payload builders untouched.
 *
 * The TS interfaces remain the authoritative compile-time contract; these
 * schemas add the runtime guard that the `as unknown as` casts in
 * `feConfigAdapter` previously lacked.
 */

const labeledOption = z.object({ label: z.string(), value: z.string() }).passthrough();

// Shared base for the typed form fields (`FormFieldConfig`). `label` is the
// render-critical leaf; `required` is the only other commonly-read base flag.
const baseField = { label: z.string().min(1), required: z.boolean().optional() };

/**
 * `FormFieldConfig` used by `eventDetailsFields`, `promoDetailsFields` and
 * `planFields`. Discriminated on `type` so each variant enforces only the
 * fields its renderer needs.
 */
export const formFieldSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('input'), name: z.string().min(1), ...baseField }).passthrough(),
    z.object({ type: z.literal('datepicker'), name: z.string().min(1), ...baseField }).passthrough(),
    z
        .object({
            type: z.literal('select'),
            name: z.string().min(1),
            options: z.array(labeledOption),
            ...baseField,
        })
        .passthrough(),
    z.object({ type: z.literal('yearPicker'), name: z.string().min(1), ...baseField }).passthrough(),
    // dimSelect derives its label/name from `dimension`/`attributeType` in
    // `ConfigDrivenFormFields`, so those two are the render-critical leaves.
    z
        .object({
            type: z.literal('dimSelect'),
            name: z.string().optional(),
            label: z.string().optional(),
            required: z.boolean().optional(),
            dimension: z.string().min(1),
            attributeType: z.string().min(1),
        })
        .passthrough(),
]);

/**
 * `PromoFieldConfig` (flat shape; its `type` enum adds `number`/`range`/
 * `offerlibrary`). `name`/`label`/`type` are dereferenced unconditionally when
 * rendering the promo form; `unitToggle`, when present, must be renderable.
 */
export const promoFieldSchema = z
    .object({
        name: z.string().min(1),
        label: z.string().min(1),
        type: z.enum(['input', 'datepicker', 'select', 'number', 'range', 'offerlibrary']),
        options: z.array(labeledOption).optional(),
        unitToggle: z
            .object({
                unitField: z.string().min(1),
                options: z.array(labeledOption).min(1),
                defaultValue: z.string().optional(),
            })
            .passthrough()
            .optional(),
        rangeLabels: z.object({ first: z.string(), second: z.string() }).passthrough().optional(),
    })
    .passthrough();

/**
 * `FormCascadeRule` (deterministic form -> metric cascade). Tolerant: only the
 * keys the cascade engine dereferences are required; `.passthrough()` keeps any
 * forward-compatible additions. `formCascadeRules` itself is optional + defaulted
 * in `feConfigSchema`, so a config without it never blocks the module.
 */
export const formCascadeRuleSchema = z
    .object({
        id: z.string().min(1),
        triggers: z.array(z.string()).min(1),
        // Gate condition (engine `RuleCondition`); structurally validated by the engine
        // (`evaluateCondition`) + the dev-time `devValidateFormCascadeRules`.
        when: z.unknown().optional(),
        effect: z
            .object({
                kind: z.literal('setMetric'),
                metricKey: z.string().min(1),
                unitField: z.string().optional(),
                formulaByUnit: z.record(z.string()).optional(),
                defaultFormula: z.string().optional(),
                variants: z.array(z.record(z.unknown())).optional(),
                inputs: z
                    .object({
                        form: z.array(z.string()),
                        metrics: z.array(z.string()),
                    })
                    .passthrough(),
            })
            .passthrough(),
    })
    .passthrough();

/**
 * `FieldRule` (declarative, cross-section field-rules engine). Tolerant: only the
 * render/route-critical leaves are required (`id`, `target`, `effect.kind`);
 * `.passthrough()` keeps the effect/condition payloads (validated structurally
 * by the engine + a dev-time validator). `fieldRules` is optional + defaulted in
 * `feConfigSchema`, so a config without it never blocks the module.
 */
export const fieldRuleSchema = z
    .object({
        id: z.string().min(1),
        target: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]),
        effect: z.object({ kind: z.string().min(1) }).passthrough(),
        when: z.unknown().optional(),
        triggers: z.array(z.string()).optional(),
    })
    .passthrough();

const labelLeaf = z.string();

/**
 * `Labels` tree. Requires the scopes/leaves the code reads (mirrors the `LABELS`
 * anchor in `labels.ts`). `.passthrough()` at every level tolerates extra or
 * not-yet-consumed label keys (e.g. `createPlanModal`); a missing consumed leaf
 * fails loud (the intended guard against `labels.x.y` being undefined).
 */
export const labelsSchema = z
    .object({
        common: z
            .object({
                breadcrumbRoot: labelLeaf,
                edit: labelLeaf,
                delete: labelLeaf,
                cancel: labelLeaf,
                save: labelLeaf,
                close: labelLeaf,
                actions: labelLeaf,
                noData: labelLeaf,
            })
            .passthrough(),
        planIndex: z
            .object({
                sectionTitle: labelLeaf,
                description: labelLeaf,
                breadcrumb: labelLeaf,
                createNewPlan: labelLeaf,
                myPlan: labelLeaf,
                fields: labelLeaf,
                selectFields: labelLeaf,
                tabs: z.object({ plans: labelLeaf, comparisonReports: labelLeaf }).passthrough(),
                table: z
                    .object({
                        planningGroups: labelLeaf,
                        createdBy: labelLeaf,
                        status: labelLeaf,
                        lastUpdated: labelLeaf,
                        dateCreated: labelLeaf,
                        planVersion: labelLeaf,
                        plans: labelLeaf,
                        createNewPlan: labelLeaf,
                        collapse: labelLeaf,
                        expand: labelLeaf,
                        editPlan: labelLeaf,
                    })
                    .passthrough(),
            })
            .passthrough(),
        planDetail: z
            .object({
                breadcrumb: labelLeaf,
                customer: labelLeaf,
                category: labelLeaf,
                year: labelLeaf,
                createNewPlan: labelLeaf,
                exitWithoutPromoSave: labelLeaf,
                operationalPlanTooltip: labelLeaf,
            })
            .passthrough(),
        editSession: z
            .object({
                editPlan: labelLeaf,
                exitEditMode: labelLeaf,
                sessionTimePrefix: labelLeaf,
                clickToExtendTooltip: labelLeaf,
                extendTooltip: labelLeaf,
                heldByOtherPrefix: labelLeaf,
                footerEditingBy: labelLeaf,
                viewEventAction: labelLeaf,
                viewPromoAction: labelLeaf,
                sessionLostTitle: labelLeaf,
                sessionLostBody: labelLeaf,
                sessionLostOk: labelLeaf,
                editPlanTitle: labelLeaf,
                editPlanQuestion: labelLeaf,
                takeOverNote: labelLeaf,
                extendTitle: labelLeaf,
                extendQuestion: labelLeaf,
                confirmYes: labelLeaf,
            })
            .passthrough()
            .optional(),
        // Optional for the same reason as `editSession`: configs predating the
        // keep-flag row actions must still validate. `adaptLabels` backfills it.
        keepFlags: z
            .object({
                removePrefix: labelLeaf,
                applyQuestion: labelLeaf,
                removeQuestion: labelLeaf,
                promoStartedTooltip: labelLeaf,
                noPromoTooltip: labelLeaf,
                mutuallyExclusiveTooltip: labelLeaf,
                keepDatesIndicatorTooltip: labelLeaf,
                keepPromoIndicatorTooltip: labelLeaf,
                confirmYes: labelLeaf,
                keepEventTitle: labelLeaf,
                keepEventNote: labelLeaf,
                keepEventRemoveTitle: labelLeaf,
                keepEventRemoveNote: labelLeaf,
                keptForEventTooltip: labelLeaf,
                keepEventIndicatorTooltip: labelLeaf,
            })
            .passthrough()
            .optional(),
        events: z
            .object({
                addNewEvent: labelLeaf,
                editEvent: labelLeaf,
                eventDetails: labelLeaf,
                productPortfolio: labelLeaf,
                addEventDetails: labelLeaf,
                editEventDetails: labelLeaf,
                addProductDetails: labelLeaf,
                editProductDetails: labelLeaf,
                update: labelLeaf,
            })
            .passthrough(),
        promos: z
            .object({
                sectionTitle: labelLeaf,
                addPromo: labelLeaf,
                editPromo: labelLeaf,
                addNewPromo: labelLeaf,
                savePromo: labelLeaf,
                updatePromo: labelLeaf,
                noPromotionsAdded: labelLeaf,
                addPromoDetails: labelLeaf,
                editPromoDetails: labelLeaf,
                addPromoTab: labelLeaf,
                refreshUplift: labelLeaf,
            })
            .passthrough(),
        modals: z
            .object({
                unsavedPromoChanges: labelLeaf,
                unsavedEventChanges: labelLeaf,
                unsavedPromoDescription: labelLeaf,
                unsavedEventDescription: labelLeaf,
                continueEditing: labelLeaf,
                discardChanges: labelLeaf,
                deletePromoSubTitle: labelLeaf,
                noPromotionsTitle: labelLeaf,
                deleteEvent: labelLeaf,
                noPromotionsDescription: labelLeaf,
            })
            .passthrough(),
        optimizePlan: z
            .object({
                toolbarButton: labelLeaf,
                modalTitle: labelLeaf,
                tabs: z.object({ scope: labelLeaf, constraints: labelLeaf }).passthrough(),
                runOptimisation: labelLeaf,
                scopeSection: labelLeaf,
                baseInputsSection: labelLeaf,
                loadingPlaceholder: labelLeaf,
                constraintsSection: labelLeaf,
                selectLevelPlaceholder: labelLeaf,
                tableError: labelLeaf,
                emptyConstraints: labelLeaf,
                level: z
                    .object({
                        addLevel: labelLeaf,
                        allLevelsAdded: labelLeaf,
                        removeLevel: labelLeaf,
                        removeAttribute: labelLeaf,
                        loading: labelLeaf,
                        error: labelLeaf,
                        empty: labelLeaf,
                    })
                    .passthrough()
                    .optional(),
                statusMessages: z
                    .object({
                        loadingConstraints: labelLeaf,
                        countingPromotions: labelLeaf,
                        startingOptimization: labelLeaf,
                    })
                    .passthrough(),
                selector: z
                    .object({
                        buttonLabel: labelLeaf,
                        searchPlaceholder: labelLeaf,
                        selectAll: labelLeaf,
                        unselectAll: labelLeaf,
                        noResults: labelLeaf,
                        cancel: labelLeaf,
                        apply: labelLeaf,
                    })
                    .passthrough(),
                notifications: z
                    .object({
                        optimizationFailed: labelLeaf,
                        infeasibleToast: labelLeaf,
                        infeasibleTagLabel: labelLeaf,
                        infeasibleTooltipFallback: labelLeaf,
                    })
                    .passthrough(),
            })
            .passthrough(),
    })
    .passthrough();

/**
 * `PromoLibraryUiConfig` — the PRESENTATION slice of the Promo Library config.
 *
 * Columns, filters, formats, paging and sort defaults all come from the backend
 * `promo_library` section (see `promoLibrarySchema`), so `fe_config` carries only
 * what the backend has no opinion about: UI copy, the filter panel's per-dimension
 * section labels, and the overflow menu.
 */
export const promoLibraryConfigSchema = z
    .object({
        filterDimensionInfo: z.record(z.unknown()).optional().default({}),
        filterKeys: z
            .array(
                z
                    .object({
                        label: z.string().min(1),
                        value: z.string().min(1),
                        dimension: z.string().min(1),
                        resolveLabelFrom: z.string().min(1).optional(),
                    })
                    .passthrough(),
            )
            .optional()
            .default([]),
        actionMenuItems: z
            .array(z.object({ key: z.string().min(1), label: z.string().min(1) }).passthrough())
            .optional()
            .default([]),
        labels: z
            .object({
                breadcrumb: labelLeaf,
                sectionTitle: labelLeaf,
                showChange: labelLeaf,
                kpis: labelLeaf,
                selectKpis: labelLeaf,
                selectAll: labelLeaf,
                deselectAll: labelLeaf,
                noData: labelLeaf,
                noDataSubtitle: labelLeaf,
                recordCount: labelLeaf,
            })
            .passthrough(),
    })
    .passthrough();
